import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../../firebase/config';
import { collection, query, where, getDocs, doc, updateDoc, getDoc, setDoc } from 'firebase/firestore';
import { signOut } from 'firebase/auth';
import { Bell, LogOut, Search, Users, UserPlus, Edit, AlertTriangle, Award } from 'lucide-react';

function MentoringCoordDashboard() {
  const navigate = useNavigate();
  const [coordinator, setCoordinator] = useState(null);
  const [batchAssignments, setBatchAssignments] = useState([]);
  const [mentors, setMentors] = useState([]);
  const [allStudents, setAllStudents] = useState([]);
  const [unassignedStudents, setUnassignedStudents] = useState([]);
  const [searchRoll, setSearchRoll] = useState('');
  const [searchedStudent, setSearchedStudent] = useState(null);
  const [selectedMentor, setSelectedMentor] = useState('');
  const [loading, setLoading] = useState(true);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [editingBatch, setEditingBatch] = useState(null);

  const BATCHES = ['A1', 'A2', 'A3', 'B1', 'B2', 'B3', 'C1', 'C2', 'C3'];

  useEffect(() => {
    loadCoordinatorData();
  }, []);

  const loadCoordinatorData = async () => {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        navigate('/staff-login');
        return;
      }

      const coordinatorDoc = await getDoc(doc(db, 'users', currentUser.email));
      if (coordinatorDoc.exists()) {
        setCoordinator(coordinatorDoc.data());

        // Load all mentors
        const mentorsQuery = query(collection(db, 'users'), where('role', '==', 'mentor'));
        const mentorsSnapshot = await getDocs(mentorsQuery);
        const mentorsData = mentorsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setMentors(mentorsData);

        // Load all students
        const studentsSnapshot = await getDocs(collection(db, 'students'));
        const studentsData = studentsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setAllStudents(studentsData);

        // Load batch assignments
        const batchAssignmentsData = [];
        for (const batch of BATCHES) {
          const batchDoc = await getDoc(doc(db, 'batchAssignments', batch));
          if (batchDoc.exists()) {
            const data = batchDoc.data();
            const batchStudents = studentsData.filter(s => s.batch === batch);
            batchAssignmentsData.push({
              batch: batch,
              ...data,
              studentCount: batchStudents.length
            });
          } else {
            const batchStudents = studentsData.filter(s => s.batch === batch);
            batchAssignmentsData.push({
              batch: batch,
              mentorEmail: null,
              mentorName: null,
              studentCount: batchStudents.length
            });
          }
        }
        setBatchAssignments(batchAssignmentsData);

        // Find unassigned students
        const unassigned = studentsData.filter(s => !s.batch || s.batch === '');
        setUnassignedStudents(unassigned);
      }

      setLoading(false);
    } catch (error) {
      console.error('Error loading coordinator data:', error);
      setLoading(false);
    }
  };

  const handleSearchStudent = async () => {
    if (!searchRoll) return;

    try {
      const studentDoc = await getDoc(doc(db, 'students', searchRoll.toUpperCase()));
      if (studentDoc.exists()) {
        const studentData = studentDoc.data();
        
        const attendanceDoc = await getDoc(doc(db, 'attendance', studentData.rollNumber));
        const attendanceData = attendanceDoc.exists() ? attendanceDoc.data() : null;

        const iaTest1Doc = await getDoc(doc(db, 'iaTest1', studentData.rollNumber));
        const iaTest2Doc = await getDoc(doc(db, 'iaTest2', studentData.rollNumber));

        setSearchedStudent({
          ...studentData,
          attendance: attendanceData,
          iaTest1: iaTest1Doc.exists() ? iaTest1Doc.data() : null,
          iaTest2: iaTest2Doc.exists() ? iaTest2Doc.data() : null
        });
      } else {
        alert('Student not found');
        setSearchedStudent(null);
      }
    } catch (error) {
      console.error('Error searching student:', error);
      alert('Error searching student');
    }
  };

  const handleAssignMentorToBatch = async (batch) => {
    if (!selectedMentor) {
      alert('Please select a mentor');
      return;
    }

    try {
      const mentorData = mentors.find(m => m.email === selectedMentor);
      
      await setDoc(doc(db, 'batchAssignments', batch), {
        batch: batch,
        mentorEmail: selectedMentor,
        mentorName: mentorData.name,
        division: batch.charAt(0),
        updatedAt: new Date(),
        updatedBy: coordinator.email
      });

      // Update mentor's assigned batch
      await updateDoc(doc(db, 'users', selectedMentor), {
        assignedBatch: batch
      });

      // Update all students in this batch to have the mentor
      const studentsInBatch = allStudents.filter(s => s.batch === batch);
      for (const student of studentsInBatch) {
        await updateDoc(doc(db, 'students', student.rollNumber), {
          mentorEmail: selectedMentor
        });
      }

      alert('Mentor assigned successfully!');
      setShowAssignModal(false);
      setEditingBatch(null);
      setSelectedMentor('');
      loadCoordinatorData();
    } catch (error) {
      console.error('Error assigning mentor:', error);
      alert('Failed to assign mentor');
    }
  };

  const handleLogout = async () => {
    await signOut(auth);
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-md">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Mentoring Coordinator Dashboard</h1>
              <p className="text-sm text-gray-600">{coordinator?.name}</p>
            </div>
            <div className="flex items-center space-x-4">
              <button className="relative p-2 hover:bg-gray-100 rounded-full">
                <Bell className="w-6 h-6 text-gray-700" />
              </button>
              <button
                onClick={handleLogout}
                className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                <LogOut className="w-4 h-4" />
                <span>Logout</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Search Bar */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Student Roll Number Lookup</h2>
          <div className="flex space-x-3">
            <input
              type="text"
              value={searchRoll}
              onChange={(e) => setSearchRoll(e.target.value.toUpperCase())}
              placeholder="Enter Roll Number (e.g., FB107)"
              className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              onKeyPress={(e) => e.key === 'Enter' && handleSearchStudent()}
            />
            <button
              onClick={handleSearchStudent}
              className="flex items-center space-x-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Search className="w-5 h-5" />
              <span>Search</span>
            </button>
          </div>
          {searchedStudent && (
            <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">
                    {searchedStudent.firstName} {searchedStudent.surname} - {searchedStudent.rollNumber}
                  </h3>
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <p className="text-gray-600">Attendance</p>
                      <p className="font-bold">{searchedStudent.attendance?.overallPercentage || 0}%</p>
                    </div>
                    <div>
                      <p className="text-gray-600">IA Test 1</p>
                      <p className="font-bold">{searchedStudent.iaTest1?.percentage || 0}%</p>
                    </div>
                    <div>
                      <p className="text-gray-600">IA Test 2</p>
                      <p className="font-bold">{searchedStudent.iaTest2?.percentage || 0}%</p>
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => setSearchedStudent(null)}
                  className="text-blue-600 hover:underline text-sm"
                >
                  Clear
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Unassigned Students Alert */}
        {unassignedStudents.length > 0 && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            <div className="flex items-center space-x-3">
              <AlertTriangle className="w-6 h-6 text-yellow-600" />
              <div>
                <p className="font-semibold text-yellow-900">
                  {unassignedStudents.length} students not assigned to any batch
                </p>
                <p className="text-sm text-yellow-700">
                  Please assign these students to batches from the student database
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Batch Assignments */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-900">Batch & Mentor Assignments</h2>
            <Users className="w-6 h-6 text-blue-600" />
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {batchAssignments.map(assignment => (
              <div
                key={assignment.batch}
                className={`border-2 rounded-lg p-4 ${
                  assignment.mentorEmail ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'
                }`}
              >
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">Batch {assignment.batch}</h3>
                    <p className="text-sm text-gray-600">{assignment.studentCount} students</p>
                  </div>
                  <button
                    onClick={() => {
                      setEditingBatch(assignment.batch);
                      setSelectedMentor(assignment.mentorEmail || '');
                      setShowAssignModal(true);
                    }}
                    className="p-2 hover:bg-white rounded transition-colors"
                  >
                    <Edit className="w-5 h-5 text-gray-600" />
                  </button>
                </div>

                {assignment.mentorEmail ? (
                  <div>
                    <p className="text-sm text-gray-600">Mentor:</p>
                    <p className="font-semibold text-gray-900">{assignment.mentorName}</p>
                    <p className="text-xs text-gray-500 mt-1">{assignment.mentorEmail}</p>
                  </div>
                ) : (
                  <div className="text-center py-2">
                    <AlertTriangle className="w-8 h-8 text-red-600 mx-auto mb-2" />
                    <p className="text-sm font-medium text-red-900">No Mentor Assigned</p>
                    <button
                      onClick={() => {
                        setEditingBatch(assignment.batch);
                        setShowAssignModal(true);
                      }}
                      className="mt-2 text-sm text-red-600 hover:underline"
                    >
                      Assign Now
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* System Alerts */}
        <div className="grid md:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Pending Verifications</h3>
              <Award className="w-6 h-6 text-orange-600" />
            </div>
            <p className="text-3xl font-bold text-gray-900">--</p>
            <p className="text-sm text-gray-600 mt-1">Achievements pending verification across all batches</p>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Students at Risk</h3>
              <AlertTriangle className="w-6 h-6 text-red-600" />
            </div>
            <p className="text-3xl font-bold text-red-600">
              {allStudents.filter(s => {
                // This would need attendance data loaded for all students
                return false; // Placeholder
              }).length}
            </p>
            <p className="text-sm text-gray-600 mt-1">Students with attendance below 75%</p>
          </div>
        </div>
      </div>

      {/* Assign Mentor Modal */}
      {showAssignModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Assign Mentor to Batch {editingBatch}
            </h2>
            
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Mentor
              </label>
              <select
                value={selectedMentor}
                onChange={(e) => setSelectedMentor(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">-- Select a Mentor --</option>
                {mentors.map(mentor => (
                  <option key={mentor.email} value={mentor.email}>
                    {mentor.name} ({mentor.email})
                    {mentor.assignedBatch && ` - Currently: ${mentor.assignedBatch}`}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => handleAssignMentorToBatch(editingBatch)}
                className="flex-1 bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700"
              >
                Assign Mentor
              </button>
              <button
                onClick={() => {
                  setShowAssignModal(false);
                  setEditingBatch(null);
                  setSelectedMentor('');
                }}
                className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MentoringCoordDashboard;